import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { AIAgentResult } from '../../models/AIAgent'
import { BaseRequest, BaseResponse } from '../models'
import { paginate } from '../utils'
import { removeEmptyItems } from '../../common/util'

const _BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

export interface GetAIAgentResultRequest extends BaseRequest {
  type: string
  resultKey: string
}

export interface ListAIAgentResultRequest extends BaseRequest {
  type?: string
  resultKey?: string
  partialKeyMatch?: boolean
}

export interface AIAgentResultUpdateRequest extends BaseRequest {
  aiAgentResultId: string
  body: {
    result?: { [key: string]: any }
    metadata?: string
  }
}

export const aiAgentResultApi = createApi({
  reducerPath: 'aiAgentResultApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['AgentResult'],
  endpoints: (builder) => ({
    getAiAgentResult: builder.query<AIAgentResult | null, GetAIAgentResultRequest>({
      query: (request) => {
        const { accountId, type, resultKey } = request
        return {
          url: `/accounts/${accountId}/ai-agent-results`,
          params: { type, resultKey },
        }
      },
      transformResponse: (response: { data: AIAgentResult[] }) => (response.data?.length ? response.data[0] : null),
    }),
    listAiAgentResult: builder.query<AIAgentResult[], ListAIAgentResultRequest>({
      query: (request) => {
        const { accountId, type, resultKey, partialKeyMatch } = request
        return {
          url: `/accounts/${accountId}/ai-agent-results`,
          params: { type, resultKey, partialKeyMatch },
        }
      },
      transformResponse: (response: { data: AIAgentResult[] }) => response.data,
      providesTags: ['AgentResult'],
    }),
    getAllAiAgentResults: builder.query<BaseResponse<AIAgentResult[]>, ListAIAgentResultRequest>({
      async queryFn(request, _queryApi, _extraOptions, fetchWithBQ) {
        const { accountId, type, resultKey, partialKeyMatch } = request
        return await paginate<AIAgentResult>(
          {
            url: `/accounts/${accountId}/ai-agent-results`,
            params: { ...(type && { type }), ...(resultKey && { resultKey }), ...(partialKeyMatch ? { partialKeyMatch } : {}) },
          },
          fetchWithBQ,
        )
      },
      providesTags: ['AgentResult'],
    }),
    updateAiAgentResult: builder.mutation<AIAgentResult, AIAgentResultUpdateRequest>({
      query: (request) => {
        const { accountId, aiAgentResultId, body } = request
        return {
          url: `/accounts/${accountId}/ai-agent-results/${aiAgentResultId}`,
          body: removeEmptyItems(body),
          method: 'PATCH',
        }
      },
      transformResponse: (response: BaseResponse<AIAgentResult>) => {
        return response.data
      },
      invalidatesTags: ['AgentResult'],
    }),
  }),
})

export const {
  useListAiAgentResultQuery,
  useLazyGetAllAiAgentResultsQuery,
  useGetAiAgentResultQuery,
  useLazyGetAiAgentResultQuery,
  useLazyListAiAgentResultQuery,
  useGetAllAiAgentResultsQuery,
  useUpdateAiAgentResultMutation,
} = aiAgentResultApi
